import { OK, TEXT_SECONDARY } from './fleetLocationStyles';

/*
 * Shared pieces of the fleet map, kept out of the component so they can be reasoned about
 * (and later tested) without a DOM.
 */

/*
 * THE FLEET READ, AT THE ROUTE THE BACKEND PINS.
 *
 * One request answers for every visible vehicle. Held here rather than beside the component's
 * other URLs because both halves of the wire assert against this literal -- the backend suite
 * pins the same string -- and a route the two ends spell differently is a map that stays
 * empty while every test on each side passes.
 */
const FLEET_POSITIONS_URL = '/uiapi/1/fleetlocation/positions/';

/*
 * VEHICLES THE CREDENTIALS REPORT AND THIS FLEET DOES NOT HOLD (OVIO-39).
 *
 * Admin-only on the server: the person who manages the keys may see everything those keys
 * report. A non-admin is refused, and the map treats that refusal as "nothing to show" rather
 * than as an error, because for them it is not one.
 */
const UNRECONCILED_URL = '/uiapi/1/fleetlocation/unreconciled/';

/**
 * What to call a vehicle on screen: the fleet's own name first, because that is what the
 * fleet says out loud. A VIN is a fallback, not a label.
 *
 * `fallback` is the LAST resort, for a vehicle carrying no name, registration or VIN at all.
 * It is passed in already translated rather than built here, because this module has no
 * translator and a hardcoded English string on an otherwise Finnish screen is the mixing
 * this exists to avoid.
 */
function labelFor(vehicle, fallback) {
  if (vehicle.name) {
    return vehicle.name;
  }
  if (vehicle.registration_number) {
    return vehicle.registration_number;
  }
  if (vehicle.vin) {
    return vehicle.vin;
  }
  return fallback || 'Vehicle ' + vehicle.id;
}

/** A credential's visible and accessible name must identify it even before it is named. */
function connectionLabel(connection, t) {
  return connection.label || (t('credentialsTitle') + ' #' + connection.id);
}

// A pin carries its own name, so the label rides in the map itself rather than only in the
// table beside it. Twenty-four characters is what fits a pin without the next truck's label
// colliding with it; a fleet naming trucks after their route runs past that easily.
const PIN_LABEL_MAX = 24;

/** The name to write ON the pin: the same answer as the table, cut to something that fits. */
function pinLabel(vehicle, fallback) {
  const label = labelFor(vehicle, fallback);
  const codepoints = Array.from(label);
  if (codepoints.length <= PIN_LABEL_MAX) {
    return label;
  }
  // The ellipsis counts toward the budget, so a cut label is never wider than an uncut one.
  return codepoints.slice(0, PIN_LABEL_MAX - 1).join('') + '…';
}

// The two status colours, imported from the style module so the card and the table cannot
// drift apart on what 'current' looks like.
const STATUS_OK = OK;
const STATUS_MUTED = TEXT_SECONDARY;

// Below this span, a bounding box is indistinguishable from a point at any useful zoom.
// About half a kilometre of latitude -- one depot, one lorry park, one vehicle.
const SINGLE_POINT_SPAN = 0.005;

// Close enough to read a street name, far enough to see where in the city it is.
const SINGLE_POINT_ZOOM = 13;

// A PIN IS DRAWN ABOVE THE POINT IT MARKS. The graphic and its label sit some thirty pixels
// up, so a vehicle sitting exactly on the edge of a tightly fitted box has its pin rendered
// outside the map -- present in the page, invisible on it. Padding a share of the spread
// rather than a fixed number of degrees keeps a depot-sized fleet framed as a depot.
const BOUNDS_PADDING = 0.12;

// AND THE LABEL EXTENDS RIGHTWARD, always. Symmetric padding frames the easternmost truck's
// marker correctly and clips its NAME, which is the one thing the label is for. Seen on a
// 375px phone, where the map is narrow enough to make it obvious; the same clipping happens
// on a desktop whenever the fleet is wide.
const RIGHT_LABEL_PADDING = 0.35;

/**
 * How to frame the fleet: fit to every pin, or centre on one.
 *
 * FITTING TO A BOX WITH NO SIZE IS THE BUG THIS EXISTS TO PREVENT. One vehicle makes a
 * bounding box of zero area, and the HERE viewmodel answers that by zooming to its maximum --
 * a blank tile with a pin in the middle of nothing. Every tenant reading a real Scania test
 * credential has exactly one located vehicle, so that was the state of every real fleet map.
 *
 * Returns a DECISION, not a HERE object, so the rule can be tested without a map: 'none'
 * leaves the opening view alone, 'single' centres and zooms, 'bounds' fits as before.
 */
function mapViewFor(points) {
  if (!points || points.length === 0) {
    return { kind: 'none' };
  }
  const lats = points.map((p) => p.lat);
  const lngs = points.map((p) => p.lng);
  const minLat = Math.min.apply(null, lats);
  const maxLat = Math.max.apply(null, lats);
  const minLng = Math.min.apply(null, lngs);
  const maxLng = Math.max.apply(null, lngs);
  if (maxLat - minLat < SINGLE_POINT_SPAN && maxLng - minLng < SINGLE_POINT_SPAN) {
    return {
      kind: 'single',
      point: { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 },
      zoom: SINGLE_POINT_ZOOM,
    };
  }
  const latPad = (maxLat - minLat) * BOUNDS_PADDING;
  const lngPad = (maxLng - minLng) * BOUNDS_PADDING;
  return {
    kind: 'bounds',
    // Named the way a HERE geo rect is: top and bottom are latitudes, left and right
    // longitudes. Returned as plain numbers so the rule stays testable without the SDK.
    bounds: {
      top: maxLat + latPad,
      left: minLng - lngPad,
      bottom: minLat - latPad,
      right: maxLng + lngPad + (maxLng - minLng) * RIGHT_LABEL_PADDING,
    },
  };
}

/**
 * Does a newly-placed pin justify moving the camera?
 *
 * THE CAMERA MUST SETTLE. Positions arrive two at a time, and re-framing on every arrival
 * makes a thirty-truck fleet lurch for a minute while nobody can read a label or click a
 * pin. Two vehicles hide this completely -- one jump looks like the map settling -- which is
 * why a browser check reporting zero errors never caught it.
 *
 * Move only when the answer would CHANGE: the first pin, or one outside what is already on
 * screen. `current` is the map's present bounding box, or null/undefined before it has one.
 */
function shouldReframe(current, point) {
  if (!current) {
    // No box yet -- either the first pin or a map that has not laid out. Framing an already
    // correct view costs a redundant animation; NOT framing the first one leaves the fleet
    // off screen entirely, so the asymmetry decides it.
    return true;
  }
  return (
    point.lat > current.top
    || point.lat < current.bottom
    || point.lng < current.left
    || point.lng > current.right
  );
}

/**
 * What the screen says about a vehicle's position, in words.
 *
 * SHARED BY THE TABLE AND THE CARD. A phone shows cards and a desktop shows a table; the
 * same vehicle must read the same on both, and a rule written twice is a rule that drifts.
 *
 * The RAW timestamp is handed to the translator, never a pre-formatted string: formatting
 * here would follow the BROWSER's language and put an American date inside a Finnish
 * sentence.
 */
function stateLabel(entry, t) {
  if (entry.state === 'ok') {
    return entry.stale
      ? t('statusLastSeen', { time: entry.position.ts })
      : t('statusCurrent');
  }
  if (entry.state === 'pending') {
    return t('statusLocating');
  }
  if (entry.state === 'no_recent') {
    return t('statusNoRecent');
  }
  if (entry.state === 'not_connected') {
    return t('statusNotConnected');
  }
  return t('statusUnavailable');
}

/** True when the position is current: fresh, and actually present. */
function isCurrent(entry) {
  return entry.state === 'ok' && !entry.stale;
}

/**
 * The two facts a phone card shows on its face.
 *
 * A card has room for a title, a subtitle and two or three rows -- not a table row. Which
 * facts survive that cut is a product decision, so it is pinned here rather than left to
 * whoever writes the markup:
 *
 *   - the STATUS is the answer the reader opened the screen for
 *   - the VIN is how they confirm this is the right truck
 *
 * The name is deliberately absent: it is already the card's title, and repeating it spends
 * the only space a phone has on something the reader can already see.
 */
function cardFieldsFor(entry, t) {
  return [
    {
      label: t('columnStatus'),
      value: stateLabel(entry, t),
      valueColor: isCurrent(entry) ? STATUS_OK : STATUS_MUTED,
    },
    { label: t('columnVin'), value: entry.vehicle.vin },
  ];
}

/**
 * Which load is the current one.
 *
 * CLAIMED WHEN A REFRESH IS REQUESTED, not when it starts running. The counter used to
 * increment inside the load itself, and loads are serialised -- so a refresh queued behind a
 * running one could not tell that one it had been superseded. The stale load ran to
 * completion, fetching a position for every vehicle in a fleet that had already been
 * replaced, each read holding a server worker, before the new load even began.
 *
 * A separate object rather than two fields on the component, because the rule is what was
 * wrong and a rule that cannot be tested on its own is a rule that stays wrong.
 */
class Generations {
  constructor() {
    this.latest = 0;
  }

  /** Take the next token and make it the current one. */
  claim() {
    this.latest += 1;
    return this.latest;
  }

  /** Is this token still the newest claim? */
  isCurrent(token) {
    return token === this.latest;
  }
}

export {
  labelFor,
  connectionLabel,
  pinLabel,
  mapViewFor,
  shouldReframe,
  stateLabel,
  isCurrent,
  cardFieldsFor,
  Generations,
  FLEET_POSITIONS_URL,
  UNRECONCILED_URL,
  PIN_LABEL_MAX,
};
